import React, { useEffect, useState, useCallback } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../../services/api';

interface ProviderPerformance {
  provider: string;
  totalRequests: number;
  successRate: number;
  averageResponseTime: number;
  errorCount: number;
  lastCheck: string;
  primaryUse?: string;
}

interface ErrorPattern {
  provider: string;
  endpoint: string;
  errorMessage: string;
  count: number;
  lastOccurrence: string;
}

interface ProblematicProvider {
  provider: string;
  successRate: number;
  errorCount: number;
  primaryUse?: string;
}

interface RecentRequest {
  id: string;
  type: 'quote' | 'candle';
  provider: string;
  success: boolean;
  statusCode: number;
  responseTimeMs: number | null;
  timestamp: string;
  error: string | null;
  symbol: string | null;
  endpoint: string | null;
  timeframe?: string | null;
  source?: string | null;
}

interface ProviderDiagnostics {
  providerStats: ProviderPerformance[];
  recentRequests: RecentRequest[];
  errorPatterns?: ErrorPattern[];
  problematicProviders?: ProblematicProvider[];
  summary: {
    totalProviders: number;
    usedProviders: number;
    avgSuccessRate: number;
    avgResponseTime: number;
    totalErrors: number;
    recentRequestsCount: number;
    quoteRequestsCount: number;
    candleRequestsCount: number;
    problematicCount?: number;
  };
}

interface HealthScore {
  provider: string;
  score: number;
  status: 'excellent' | 'good' | 'degraded' | 'critical';
  factors: {
    successRate: { value: number; weight: number; score: number };
    latency: { value: number; weight: number; score: number };
    errorCount: { value: number; weight: number; score: number };
    availability: { value: number; weight: number; score: number };
  };
  recommendation: string;
}

interface Anomaly {
  provider: string;
  anomalyType: 'latency_spike' | 'error_spike' | 'complete_failure' | 'degradation';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  detectedAt: string;
  baseline: number;
  current: number;
  percentChange: number;
}

interface SLAStatus {
  provider: string;
  target: {
    successRate: number;
    maxLatency: number;
    maxErrorRate: number;
  };
  actual: {
    successRate: number;
    latency: number;
    errorRate: number;
  };
  breached: boolean;
}

interface ChartData {
  latencyData: Array<{ timestamp: string; [provider: string]: number | string }>;
  successRateData: Array<{ timestamp: string; [provider: string]: number | string }>;
  requestVolumeData: Array<{ timestamp: string; [provider: string]: number | string }>;
}

const CHART_COLORS = [
  '#06B6D4', // cyan
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#14B8A6', // teal
  '#F97316', // orange
  '#6366F1', // indigo
  '#84CC16'  // lime
];

/**
 * Provider Performance Charts Component
 * Enhanced with real charts, health scores, anomaly detection, and auto-refresh
 */
const ProviderPerformanceCharts: React.FC = () => {
  // Basic state
  const [performance, setPerformance] = useState<ProviderPerformance[]>([]);
  const [recentRequests, setRecentRequests] = useState<RecentRequest[]>([]);
  const [errorPatterns, setErrorPatterns] = useState<ErrorPattern[]>([]);
  const [problematicProviders, setProblematicProviders] = useState<ProblematicProvider[]>([]);
  const [summary, setSummary] = useState<ProviderDiagnostics['summary'] | null>(null);
  
  // New state for enhanced features
  const [healthScores, setHealthScores] = useState<HealthScore[]>([]);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [slaStatuses, setSlaStatuses] = useState<SLAStatus[]>([]);
  const [chartData, setChartData] = useState<ChartData>({
    latencyData: [],
    successRateData: [],
    requestVolumeData: []
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [requestLimit, setRequestLimit] = useState('50');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval] = useState(30000); // 30 seconds
  
  // Filter state
  const [providerFilter, setProviderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failure'>('all');
  const [showOnlyAnomalies, setShowOnlyAnomalies] = useState(false);
  
  // Chart selection
  const [selectedProvidersForChart, setSelectedProvidersForChart] = useState<string[]>([]);

  const fetchPerformance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all data in parallel
      const [diagnosticsRes, healthRes, anomaliesRes, slaRes, chartDataRes] = await Promise.all([
        api.get(`/admin/provider-diagnostics?range=${timeRange}&limit=${requestLimit}`).catch(() => ({ data: { success: false, data: { providerStats: [], recentRequests: [], summary: { totalProviders: 0, usedProviders: 0, avgSuccessRate: 0, avgResponseTime: 0, totalErrors: 0, recentRequestsCount: 0, quoteRequestsCount: 0, candleRequestsCount: 0 } } } })),
        api.get(`/admin/provider-performance/health-scores?range=${timeRange}`).catch(() => ({ data: { success: false, data: { healthScores: [] } } })),
        api.get(`/admin/provider-performance/anomalies?range=${timeRange}`).catch(() => ({ data: { success: false, data: { anomalies: [] } } })),
        api.get(`/admin/provider-performance/sla-status?range=${timeRange}`).catch(() => ({ data: { success: false, data: { slaStatuses: [] } } })),
        api.get(`/admin/provider-performance/chart-data?range=${timeRange}&granularity=hourly`).catch(() => ({ data: { success: false, data: { chartData: [] } } }))
      ]);
      
      const diagnostics: ProviderDiagnostics = diagnosticsRes.data.data || {
        providerStats: [],
        recentRequests: [],
        summary: {
          totalProviders: 0,
          usedProviders: 0,
          avgSuccessRate: 0,
          avgResponseTime: 0,
          totalErrors: 0,
          recentRequestsCount: 0,
          quoteRequestsCount: 0,
          candleRequestsCount: 0
        }
      };
      
      setPerformance(diagnostics.providerStats || []);
      setRecentRequests(diagnostics.recentRequests || []);
      setErrorPatterns(diagnostics.errorPatterns || []);
      setProblematicProviders(diagnostics.problematicProviders || []);
      setSummary(diagnostics.summary || null);
      setHealthScores(healthRes.data.data?.healthScores || []);
      setAnomalies(anomaliesRes.data.data?.anomalies || []);
      setSlaStatuses(slaRes.data.data?.slaStatuses || []);
      setChartData(chartDataRes.data.data?.chartData || { latencyData: [], successRateData: [], requestVolumeData: [] });
      
      // Auto-select top 5 providers for charts if none selected
      if (selectedProvidersForChart.length === 0 && diagnostics.providerStats) {
        const topProviders = diagnostics.providerStats
          .filter(p => p.totalRequests > 0)
          .sort((a, b) => b.totalRequests - a.totalRequests)
          .slice(0, 5)
          .map(p => p.provider);
        setSelectedProvidersForChart(topProviders);
      }
      
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e?.message || 'Failed to load provider performance data');
    } finally {
      setLoading(false);
    }
  }, [timeRange, requestLimit, selectedProvidersForChart.length]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchPerformance();
    }, refreshInterval);
    
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchPerformance]);

  const getProviderStatusColor = (successRate: number) => {
    if (successRate >= 0.95) return 'text-green-400';
    if (successRate >= 0.8) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getProviderStatusBadge = (successRate: number, totalRequests: number) => {
    if (totalRequests === 0) return 'bg-gray-500 text-white';
    if (successRate >= 0.95) return 'bg-green-500 text-white';
    if (successRate >= 0.8) return 'bg-yellow-500 text-black';
    return 'bg-red-500 text-white';
  };

  const getProviderStatusLabel = (successRate: number, totalRequests: number) => {
    if (totalRequests === 0) return 'Not Used';
    if (successRate >= 0.95) return 'Excellent';
    if (successRate >= 0.8) return 'Good';
    return 'Poor';
  };

  const getHealthColor = (score: number) => {
    if (score >= 90) return '#10B981'; // green
    if (score >= 75) return '#F59E0B'; // amber
    if (score >= 50) return '#F97316'; // orange
    return '#EF4444'; // red
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      default: return 'bg-blue-500 text-white';
    }
  };

  const toggleProviderForChart = (provider: string) => {
    setSelectedProvidersForChart(prev =>
      prev.includes(provider)
        ? prev.filter(p => p !== provider)
        : [...prev, provider]
    );
  };

  const exportToCSV = () => {
    const csvRows = [
      ['Provider', 'Total Requests', 'Success Rate', 'Avg Response Time', 'Error Count', 'Last Check', 'Health Score', 'Primary Use']
    ];

    performance.forEach(p => {
      const health = healthScores.find(h => h.provider === p.provider);
      csvRows.push([
        p.provider,
        p.totalRequests.toString(),
        `${(p.successRate * 100).toFixed(2)}%`,
        `${p.averageResponseTime}ms`,
        p.errorCount.toString(),
        p.lastCheck,
        health ? health.score.toString() : 'N/A',
        p.primaryUse || 'N/A'
      ]);
    });

    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `provider-performance-${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Apply filters
  const filteredPerformance = performance.filter(p => {
    if (providerFilter && p.provider !== providerFilter) return false;
    if (showOnlyAnomalies && !anomalies.some(a => a.provider === p.provider)) return false;
    return true;
  });

  const filteredRequests = recentRequests.filter(r => {
    if (providerFilter && r.provider !== providerFilter) return false;
    if (statusFilter === 'success' && !r.success) return false;
    if (statusFilter === 'failure' && r.success) return false;
    return true;
  });

  // Health status summary
  const healthSummary = {
    excellent: healthScores.filter(h => h.status === 'excellent').length,
    good: healthScores.filter(h => h.status === 'good').length,
    degraded: healthScores.filter(h => h.status === 'degraded').length,
    critical: healthScores.filter(h => h.status === 'critical').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Provider Performance</h2>
          <p className="text-neutral-400">Real-time monitoring of API provider performance</p>
          {lastRefresh && (
            <p className="text-xs text-neutral-500 mt-1">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="autoRefresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="autoRefresh" className="text-sm text-neutral-300">
              Auto-refresh ({refreshInterval / 1000}s)
            </label>
          </div>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <button
            onClick={fetchPerformance}
            className="btn btn-primary px-4 py-2"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button
            onClick={exportToCSV}
            className="btn btn-outline px-4 py-2"
          >
            Export CSV
          </button>
        </div>
      </div>

      {loading && !lastRefresh ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-neutral-400">Loading performance data...</p>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={fetchPerformance}
            className="btn btn-outline px-4 py-2"
          >
            Retry
          </button>
        </div>
      ) : performance.length === 0 && recentRequests.length === 0 ? (
        <div className="card bg-neutral-800/50 border border-neutral-700 p-12 text-center">
          <div className="mx-auto w-16 h-16 bg-neutral-700 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No Performance Data Available</h3>
          <p className="text-neutral-400 mb-4 max-w-md mx-auto">
            Provider performance tracking data is not yet available. The system needs to collect API request metrics before displaying analytics.
          </p>
          <p className="text-sm text-neutral-500">
            Data will appear here once the system starts making API requests to external providers.
          </p>
        </div>
      ) : (
        <>
          {/* Performance Anomalies / Alerts Panel */}
          {anomalies.length > 0 && (
            <div className="card bg-red-900/20 border border-red-500/50 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-red-400">
                  Active Performance Alerts ({anomalies.length})
                </h3>
                <button
                  onClick={() => setShowOnlyAnomalies(!showOnlyAnomalies)}
                  className="text-xs text-neutral-400 hover:text-white"
                >
                  {showOnlyAnomalies ? 'Show All' : 'Filter by Anomalies'}
                </button>
              </div>
              <div className="space-y-2">
                {anomalies.slice(0, 5).map((alert, idx) => (
                  <div key={idx} className="bg-neutral-800 rounded p-3 flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs rounded ${getSeverityColor(alert.severity)}`}>
                          {alert.severity.toUpperCase()}
                        </span>
                        <span className="text-white font-medium">{alert.provider}</span>
                        <span className="text-xs text-neutral-500">{alert.anomalyType.replace('_', ' ')}</span>
                      </div>
                      <p className="text-sm text-neutral-300">{alert.description}</p>
                      <p className="text-xs text-neutral-500 mt-1">
                        Change: {alert.percentChange >= 0 ? '+' : ''}{alert.percentChange.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                ))}
                {anomalies.length > 5 && (
                  <p className="text-xs text-neutral-400 text-center mt-2">
                    ...and {anomalies.length - 5} more anomalies
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Health Status Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card bg-green-900/30 border border-green-500/30 p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{healthSummary.excellent}</p>
              <p className="text-xs text-neutral-400">Excellent</p>
            </div>
            <div className="card bg-blue-900/30 border border-blue-500/30 p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">{healthSummary.good}</p>
              <p className="text-xs text-neutral-400">Good</p>
            </div>
            <div className="card bg-yellow-900/30 border border-yellow-500/30 p-4 text-center">
              <p className="text-3xl font-bold text-yellow-400">{healthSummary.degraded}</p>
              <p className="text-xs text-neutral-400">Degraded</p>
            </div>
            <div className="card bg-red-900/30 border border-red-500/30 p-4 text-center">
              <p className="text-3xl font-bold text-red-400">{healthSummary.critical}</p>
              <p className="text-xs text-neutral-400">Critical</p>
            </div>
          </div>

          {/* Performance Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Total Providers</h3>
              <p className="text-2xl font-bold text-white">{summary?.totalProviders || performance.length}</p>
              <p className="text-xs text-neutral-500 mt-1">
                {summary?.usedProviders || performance.filter(p => p.totalRequests > 0).length} active
              </p>
            </div>
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Avg Success Rate</h3>
              <p className={`text-2xl font-bold ${summary && summary.avgSuccessRate >= 95 ? 'text-green-400' : summary && summary.avgSuccessRate >= 80 ? 'text-yellow-400' : 'text-red-400'}`}>
                {summary ? `${summary.avgSuccessRate.toFixed(1)}%` : 'N/A'}
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                across {summary?.usedProviders || 0} providers
              </p>
            </div>
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Avg Response Time</h3>
              <p className={`text-2xl font-bold ${summary && summary.avgResponseTime <= 200 ? 'text-green-400' : summary && summary.avgResponseTime <= 500 ? 'text-yellow-400' : 'text-red-400'}`}>
                {summary ? `${summary.avgResponseTime}ms` : 'N/A'}
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                {summary && summary.avgResponseTime <= 200 ? 'Excellent' : summary && summary.avgResponseTime <= 500 ? 'Good' : 'Needs attention'}
              </p>
            </div>
            <div className="card bg-neutral-900 p-4">
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Total Errors</h3>
              <p className="text-2xl font-bold text-red-400">
                {(summary?.totalErrors || 0).toLocaleString()}
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                in {timeRange === '1h' ? 'last hour' : timeRange === '24h' ? 'last 24h' : timeRange === '7d' ? 'last 7 days' : 'last 30 days'}
              </p>
            </div>
            <div className={`card p-4 ${problematicProviders.length > 0 ? 'bg-red-900/30 border border-red-500/50' : 'bg-neutral-900'}`}>
              <h3 className="text-sm font-medium text-neutral-400 mb-2">Provider Health</h3>
              <p className={`text-2xl font-bold ${problematicProviders.length === 0 ? 'text-green-400' : 'text-red-400'}`}>
                {problematicProviders.length === 0 ? 'Healthy' : `${problematicProviders.length} Issues`}
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                {problematicProviders.length === 0 ? 'All providers operational' : 'See alerts above'}
              </p>
            </div>
          </div>

          {/* Real Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Response Time Trends Chart */}
            <div className="card bg-neutral-900 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Response Time Trends</h3>
                <div className="text-xs text-neutral-400">
                  Select providers to display:
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {performance.filter(p => p.totalRequests > 0).map((p, idx) => (
                  <button
                    key={p.provider}
                    onClick={() => toggleProviderForChart(p.provider)}
                    className={`px-2 py-1 text-xs rounded ${
                      selectedProvidersForChart.includes(p.provider)
                        ? 'bg-cyan-500 text-white'
                        : 'bg-neutral-800 text-neutral-400'
                    }`}
                    style={
                      selectedProvidersForChart.includes(p.provider)
                        ? { backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }
                        : undefined
                    }
                  >
                    {p.provider}
                  </button>
                ))}
              </div>
              {chartData.latencyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData.latencyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="timestamp" 
                      stroke="#9CA3AF"
                      tickFormatter={(value) => new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    />
                    <YAxis 
                      stroke="#9CA3AF" 
                      label={{ value: 'Response Time (ms)', angle: -90, position: 'insideLeft', style: { fill: '#9CA3AF' } }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelFormatter={(value) => new Date(value).toLocaleString()}
                    />
                    <Legend />
                    {selectedProvidersForChart.map((provider, idx) => (
                      <Line
                        key={provider}
                        type="monotone"
                        dataKey={provider}
                        stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center">
                  <p className="text-neutral-400">No data available for selected time range</p>
                </div>
              )}
            </div>

            {/* Success Rate Trends Chart */}
            <div className="card bg-neutral-900 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Success Rate Trends</h3>
              {chartData.successRateData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.successRateData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="timestamp" 
                      stroke="#9CA3AF"
                      tickFormatter={(value) => new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    />
                    <YAxis 
                      stroke="#9CA3AF" 
                      label={{ value: 'Success Rate (%)', angle: -90, position: 'insideLeft', style: { fill: '#9CA3AF' } }}
                      domain={[0, 100]}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelFormatter={(value) => new Date(value).toLocaleString()}
                    />
                    <Legend />
                    {selectedProvidersForChart.map((provider, idx) => (
                      <Bar
                        key={provider}
                        dataKey={provider}
                        fill={CHART_COLORS[idx % CHART_COLORS.length]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center">
                  <p className="text-neutral-400">No data available for selected time range</p>
                </div>
              )}
            </div>
          </div>

          {/* Provider Health Scores */}
          {healthScores.length > 0 && (
            <div className="card bg-neutral-900 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Provider Health Scores</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {healthScores.slice(0, 10).map((health) => (
                  <div key={health.provider} className="bg-neutral-800 rounded-lg p-4">
                    <div className="text-center mb-3">
                      <div className="relative inline-flex items-center justify-center">
                        <svg className="w-20 h-20" viewBox="0 0 80 80">
                          <circle
                            cx="40"
                            cy="40"
                            r="35"
                            fill="none"
                            stroke="#374151"
                            strokeWidth="5"
                          />
                          <circle
                            cx="40"
                            cy="40"
                            r="35"
                            fill="none"
                            stroke={getHealthColor(health.score)}
                            strokeWidth="5"
                            strokeDasharray={`${(health.score / 100) * 220} 220`}
                            transform="rotate(-90 40 40)"
                            strokeLinecap="round"
                          />
                        </svg>
                        <span className="absolute text-lg font-bold text-white">
                          {health.score}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-white text-center">{health.provider}</p>
                    <p className={`text-xs text-center mt-1 ${
                      health.status === 'excellent' ? 'text-green-400' :
                      health.status === 'good' ? 'text-blue-400' :
                      health.status === 'degraded' ? 'text-yellow-400' :
                      'text-red-400'
                    }`}>
                      {health.status.toUpperCase()}
                    </p>
                    <p className="text-xs text-neutral-400 mt-2 text-center" title={health.recommendation}>
                      {health.recommendation.substring(0, 40)}...
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SLA Monitoring */}
          {slaStatuses.some(s => s.breached) && (
            <div className="card bg-orange-900/20 border border-orange-500/50 p-6">
              <h3 className="text-lg font-semibold text-orange-400 mb-4">SLA Breaches</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {slaStatuses.filter(s => s.breached).map((sla) => (
                  <div key={sla.provider} className="bg-neutral-800 rounded-lg p-4">
                    <h4 className="text-white font-medium mb-2">{sla.provider}</h4>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-neutral-400">Success Rate:</span>
                        <span className={sla.actual.successRate < sla.target.successRate ? 'text-red-400' : 'text-green-400'}>
                          {sla.actual.successRate.toFixed(1)}% / {sla.target.successRate}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-400">Latency:</span>
                        <span className={sla.actual.latency > sla.target.maxLatency ? 'text-red-400' : 'text-green-400'}>
                          {Math.round(sla.actual.latency)}ms / {sla.target.maxLatency}ms
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-400">Error Rate:</span>
                        <span className={sla.actual.errorRate > sla.target.maxErrorRate ? 'text-red-400' : 'text-green-400'}>
                          {sla.actual.errorRate.toFixed(1)}% / {sla.target.maxErrorRate}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="card bg-neutral-900 p-4">
            <div className="flex gap-4 flex-wrap">
              <div>
                <label className="text-sm text-neutral-400 block mb-1">Provider Filter</label>
                <select
                  value={providerFilter}
                  onChange={(e) => setProviderFilter(e.target.value)}
                  className="px-3 py-2 bg-neutral-800 border border-neutral-700 rounded text-white"
                >
                  <option value="">All Providers</option>
                  {performance.map(p => (
                    <option key={p.provider} value={p.provider}>{p.provider}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-neutral-400 block mb-1">Status Filter</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | 'success' | 'failure')}
                  className="px-3 py-2 bg-neutral-800 border border-neutral-700 rounded text-white"
                >
                  <option value="all">All Status</option>
                  <option value="success">Success Only</option>
                  <option value="failure">Failure Only</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showOnlyAnomalies}
                    onChange={(e) => setShowOnlyAnomalies(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-neutral-300">Show only providers with anomalies</span>
                </label>
              </div>
            </div>
          </div>

          {/* Provider Status Table */}
          <div className="card bg-neutral-900 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Provider Status</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-300 border-b border-neutral-800">
                    <th className="py-3 pr-4">Provider</th>
                    <th className="py-3 pr-4">Primary Use</th>
                    <th className="py-3 pr-4">Health</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4">Success Rate</th>
                    <th className="py-3 pr-4">Avg Response</th>
                    <th className="py-3 pr-4">Requests</th>
                    <th className="py-3 pr-4">Errors</th>
                    <th className="py-3 pr-4">Last Check</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {filteredPerformance.map((provider) => {
                    const health = healthScores.find(h => h.provider === provider.provider);
                    return (
                      <tr key={provider.provider} className="hover:bg-neutral-800">
                        <td className="py-3 pr-4 text-white font-medium">
                          {provider.provider}
                        </td>
                        <td className="py-3 pr-4 text-neutral-400 text-xs max-w-[200px]">
                          {provider.primaryUse || 'Data Provider'}
                        </td>
                        <td className="py-3 pr-4">
                          {health ? (
                            <span className="font-mono text-white">{health.score}</span>
                          ) : (
                            <span className="text-neutral-500">-</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${getProviderStatusBadge(provider.successRate, provider.totalRequests)}`}>
                            {getProviderStatusLabel(provider.successRate, provider.totalRequests)}
                          </span>
                        </td>
                        <td className={`py-3 pr-4 ${provider.totalRequests === 0 ? 'text-neutral-500' : getProviderStatusColor(provider.successRate)}`}>
                          {provider.totalRequests === 0 ? 'N/A' : `${(provider.successRate * 100).toFixed(1)}%`}
                        </td>
                        <td className="py-3 pr-4 text-neutral-300">
                          {provider.totalRequests === 0 ? 'N/A' : `${provider.averageResponseTime.toFixed(0)}ms`}
                        </td>
                        <td className="py-3 pr-4 text-neutral-300">
                          {provider.totalRequests.toLocaleString()}
                        </td>
                        <td className="py-3 pr-4 text-red-400">
                          {provider.errorCount.toLocaleString()}
                        </td>
                        <td className="py-3 pr-4 text-neutral-300 text-xs">
                          {provider.totalRequests === 0 ? 'Never' : new Date(provider.lastCheck).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Error Patterns */}
          {errorPatterns.length > 0 && (
            <div className="card bg-neutral-900 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Common Error Patterns</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-neutral-300 border-b border-neutral-800">
                      <th className="py-3 pr-4">Provider</th>
                      <th className="py-3 pr-4">Endpoint</th>
                      <th className="py-3 pr-4">Error</th>
                      <th className="py-3 pr-4">Count</th>
                      <th className="py-3 pr-4">Last Seen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {errorPatterns.map((err, idx) => (
                      <tr key={idx} className="hover:bg-neutral-800">
                        <td className="py-3 pr-4 text-white font-medium">{err.provider}</td>
                        <td className="py-3 pr-4 text-neutral-400 text-xs">{err.endpoint}</td>
                        <td className="py-3 pr-4 text-red-400 text-xs max-w-[300px] truncate" title={err.errorMessage}>
                          {err.errorMessage}
                        </td>
                        <td className="py-3 pr-4 text-neutral-300">{err.count.toLocaleString()}</td>
                        <td className="py-3 pr-4 text-neutral-300 text-xs">
                          {new Date(err.lastOccurrence).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent Request Log */}
          <div className="card bg-neutral-900 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Recent Request Log</h3>
                <p className="text-sm text-neutral-400 mt-1">
                  Shows which provider was used for the last {filteredRequests.length} quote/candle requests
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-neutral-400">
                  Limit:
                  <select
                    value={requestLimit}
                    onChange={(e) => setRequestLimit(e.target.value)}
                    className="ml-2 px-2 py-1 bg-neutral-800 border border-neutral-700 rounded text-white"
                  >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                  </select>
                </label>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-300 border-b border-neutral-800">
                    <th className="py-3 pr-4">Timestamp</th>
                    <th className="py-3 pr-4">Type</th>
                    <th className="py-3 pr-4">Provider</th>
                    <th className="py-3 pr-4">Symbol</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4">Response Time</th>
                    <th className="py-3">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {filteredRequests.length > 0 ? (
                    filteredRequests.map((req) => (
                      <tr key={req.id} className="hover:bg-neutral-800">
                        <td className="py-3 pr-4 text-neutral-300">
                          {new Date(req.timestamp).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </td>
                        <td className="py-3 pr-4">
                          {req.type === 'quote' ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-medium">
                              Quote
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-orange-500/20 text-orange-400 text-xs font-medium">
                              Candle
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-white font-medium">{req.provider}</span>
                          {req.timeframe && (
                            <span className="ml-2 text-xs text-neutral-500">({req.timeframe})</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          {req.symbol ? (
                            <span className="text-cyan-400 font-semibold">{req.symbol}</span>
                          ) : (
                            <span className="text-neutral-600 text-xs">—</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          {req.success ? (
                            <span className="inline-flex items-center gap-1 text-green-400">
                              <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                              {req.statusCode}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-400">
                              <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                              {req.statusCode}
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-neutral-300">
                          {req.responseTimeMs !== null ? (
                            <span className="font-mono">{req.responseTimeMs}ms</span>
                          ) : (
                            <span className="text-neutral-600 text-xs">—</span>
                          )}
                        </td>
                        <td className="py-3 text-red-400">
                          {req.error ? (
                            <span className="text-xs" title={req.error}>
                              {req.error.length > 50 ? `${req.error.substring(0, 50)}...` : req.error}
                            </span>
                          ) : (
                            <span className="text-neutral-600 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-neutral-400">
                        No recent requests found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProviderPerformanceCharts;
